/**
 * The navigation model: what a role can see, and in what order.
 *
 * This lives apart from `Shell` because three things need the same answer and
 * had been deriving it separately — the sidebar, the logo link in the top bar,
 * and the redirect from "/" after signing in. When they disagreed, a signage
 * manager got a sidebar with one section, a logo that linked to a dashboard
 * they cannot open, and a landing screen asking them to claim a desk. One
 * ordered list, read three times, is what stops that.
 *
 * Order is deliberate: it is both the sidebar order and the priority used to
 * pick a landing screen.
 */

import type { ReactNode } from 'react'
import {
  Archive,
  BarChart3,
  Building2,
  CircleHelp,
  ClipboardCheck,
  FileStack,
  Images,
  LayoutDashboard,
  ListVideo,
  Monitor,
  ScrollText,
  Settings as SettingsIcon,
  ShieldCheck,
  Tablet,
  Users,
} from 'lucide-react'

export interface NavItem {
  label: string
  to: string
  icon: ReactNode
  permission?: string
  anyOf?: string[]
  /**
   * Other path prefixes that belong to this item. A stay's page is opened
   * from the dashboard and is part of it, so the dashboard stays the current
   * item there rather than the rail showing nothing current at all.
   */
  also?: string[]
}

export interface NavSection {
  label: string
  items: NavItem[]
}

/** Rail icons, one size: 18px reads as an icon at a glance, 16px as a glyph. */
const ICON = 'h-[18px] w-[18px] shrink-0'

export const SECTIONS: NavSection[] = [
  {
    label: 'Workspace',
    items: [
      {
        label: 'Desk View',
        to: '/workspace',
        icon: <ClipboardCheck className={ICON} />,
        permission: 'capture:read',
      },
      {
        label: 'Dashboard',
        to: '/dashboard',
        icon: <LayoutDashboard className={ICON} />,
        permission: 'documents:read',
        also: ['/stays'],
      },
      {
        label: 'Archive',
        to: '/archive',
        icon: <Archive className={ICON} />,
        permission: 'documents:read',
      },
    ],
  },
  {
    label: 'Signage',
    items: [
      {
        label: 'Media',
        to: '/signage/media',
        icon: <Images className={ICON} />,
        permission: 'signage:read',
      },
      {
        label: 'Playlists',
        to: '/signage/playlists',
        icon: <ListVideo className={ICON} />,
        permission: 'signage:read',
      },
      {
        label: 'Schedules',
        to: '/signage/schedules',
        icon: <Monitor className={ICON} />,
        permission: 'signage:read',
      },
    ],
  },
  {
    label: 'Administration',
    items: [
      {
        label: 'Users',
        to: '/admin/users',
        icon: <Users className={ICON} />,
        permission: 'users:read',
      },
      {
        label: 'Roles',
        to: '/admin/roles',
        icon: <ShieldCheck className={ICON} />,
        permission: 'roles:read',
      },
      {
        label: 'Property & Desks',
        to: '/admin/desks',
        icon: <Building2 className={ICON} />,
        permission: 'desks:read',
      },
      {
        label: 'Tablets',
        to: '/admin/devices',
        icon: <Tablet className={ICON} />,
        permission: 'devices:read',
      },
      {
        label: 'Templates',
        to: '/admin/templates',
        icon: <FileStack className={ICON} />,
        permission: 'templates:read',
      },
      {
        label: 'Reports',
        to: '/admin/reports',
        icon: <BarChart3 className={ICON} />,
        permission: 'reports:read',
      },
      {
        label: 'Audit',
        to: '/admin/audit',
        icon: <ScrollText className={ICON} />,
        permission: 'audit:read',
      },
      {
        label: 'Settings',
        to: '/admin/settings',
        icon: <SettingsIcon className={ICON} />,
        permission: 'settings:read',
      },
    ],
  },
]

/**
 * Help, which every role has. Drawn at the foot of the rail beside My account
 * rather than in SECTIONS, so it never decides where anyone lands and a role
 * with no sections still has no sections.
 */
export const HELP_ITEM: NavItem = {
  label: 'Help',
  to: '/help',
  icon: <CircleHelp className={ICON} />,
}

type Can = (...permissions: string[]) => boolean

/** The sections this user can see, with the items they cannot removed. */
export function visibleSections(can: Can, canAny: Can): NavSection[] {
  return SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) =>
      item.permission ? can(item.permission) : item.anyOf ? canAny(...item.anyOf) : true,
    ),
  })).filter((section) => section.items.length > 0)
}

/**
 * Where "/" and the logo go for this user: the first screen their role can
 * actually use.
 *
 * The fallback only matters for a role with no navigation at all, which the
 * permission catalogue does not currently allow — but returning a real route
 * beats returning undefined into a router.
 */
export function landingPath(can: Can, canAny: Can): string {
  return visibleSections(can, canAny)[0]?.items[0]?.to ?? '/workspace'
}

/** Whether `path` is this item's screen or one of the screens under it. */
export function isCurrent(item: NavItem, path: string): boolean {
  return [item.to, ...(item.also ?? [])].some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  )
}

/**
 * The section and item a path belongs to: the rail's current item and the
 * top bar's breadcrumb, from the same list so they cannot disagree.
 */
export function locate(
  path: string,
  sections: NavSection[] = SECTIONS,
): { section: NavSection; item: NavItem } | null {
  for (const section of sections) {
    const item = section.items.find((candidate) => isCurrent(candidate, path))
    if (item) return { section, item }
  }
  return null
}
